/**
 * Live market data from the Bank of Israel statistics service (SDMX, CSV format).
 *
 * Three dataflows cover everything the engine prices against:
 * - `BR` — the Bank of Israel interest rate; prime is that rate plus 1.5 points.
 * - `ZCM` — inflation expectations and the zero-coupon government curve (monthly averages).
 * - `BIR_MRTG_99` — average rates on new housing loans, and on all-purpose loans secured on a home.
 * - `BIR` — average rates on other new loans, among them unsecured consumer loans to households.
 *
 * This module only parses: it has no network or browser dependency, so the Cloudflare Worker that
 * fetches the files, the dev server and the tests all share it. Every figure is validated against a
 * plausible range and left out when it fails, so a format change upstream degrades to the seeded
 * values instead of feeding nonsense into the engine.
 */

const BASE = 'https://edge.boi.gov.il/FusionEdgeServer/sdmx/v2/data/dataflow/BOI.STATISTICS';

/** The prime rate is set by convention at the Bank of Israel rate plus 1.5 points. */
export const PRIME_SPREAD = 0.015;

/** A dated figure, as a decimal fraction (0.0475 for 4.75%). */
export interface Observation {
  readonly value: number;
  /** The observation's period as published: `YYYY-MM-DD` for daily series, `YYYY-MM` for monthly. */
  readonly asOf: string;
}

export interface MarketMonth {
  /** `YYYY-MM`. */
  readonly month: string;
  readonly prime?: number;
  readonly fixedUnlinked?: number;
  readonly fixedLinked?: number;
  readonly variableLinked?: number;
}

/** How many months of history the trend shows. */
export const HISTORY_MONTHS = 12;

export interface LiveMarket {
  /** When the figures were read from the Bank of Israel, ISO timestamp. */
  readonly fetchedAt: string;
  readonly boiRate?: Observation;
  /**
   * Derived from {@link boiRate}. `since` is the first day the current rate applied, or absent when
   * the rate has not changed within the fetched window and the real date is therefore unknown.
   */
  readonly prime?: Observation & { readonly since?: string };
  /** 5-year inflation expectation implied by the bond market (breakeven). */
  readonly inflationExpectation?: Observation;
  /** 5-year zero-coupon nominal government yield — the anchor for 5-year variable unlinked tracks. */
  readonly bondYield5y?: Observation;
  /** 5-year zero-coupon CPI-linked government yield. */
  readonly linkedYield5y?: Observation;
  /** Average all-in rates on new housing loans in the latest published month. */
  readonly housing?: {
    readonly asOf: string;
    readonly fixedUnlinked?: number;
    readonly fixedLinked?: number;
    readonly variableLinked?: number;
    /** Average margin of unlinked variable (prime-based) loans over their benchmark. */
    readonly primeMargin?: number;
  };
  /** Average rates on all-purpose loans secured on a home, unlinked. */
  readonly allPurpose?: {
    readonly asOf: string;
    readonly fixedUnlinked?: number;
    /** Average all-in variable rate less prime at the end of the same month. */
    readonly variableMarginOverPrime?: number;
  };
  /**
   * Up to twelve months of published housing averages with the prime rate at the end of each month,
   * oldest first — the trend a borrower deciding when to act wants to see.
   */
  readonly history?: readonly MarketMonth[];
  /** Average rate on new unsecured consumer loans (other than overdrafts) to households, unlinked. */
  readonly consumerUnsecured?: Observation;
  /** Figures that could not be read and fall back to the seeded values. */
  readonly missing: readonly string[];
}

/** The three files to fetch, starting far enough back to reach the latest monthly publication. */
export function boiSources(now: Date): Record<keyof BoiFiles, string> {
  const monthsAgo = (months: number) => {
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - months, 1));
    return date.toISOString().slice(0, 10);
  };
  return {
    br: `${BASE}/BR/1.0?startperiod=${monthsAgo(HISTORY_MONTHS + 1)}&format=csv`,
    zcm: `${BASE}/ZCM/1.0?startperiod=${monthsAgo(4).slice(0, 7)}&format=csv`,
    mortgages: `${BASE}/BIR_MRTG_99/1.0?startperiod=${monthsAgo(HISTORY_MONTHS + 1).slice(0, 7)}&format=csv`,
    loans: `${BASE}/BIR/1.0?startperiod=${monthsAgo(6).slice(0, 7)}&format=csv`,
  };
}

export type CsvRow = Readonly<Record<string, string>>;

/** RFC 4180 CSV with a header row; quoted fields may contain commas, quotes and newlines. */
export function parseCsv(text: string): CsvRow[] {
  const records: string[][] = [];
  let field = '';
  let record: string[] = [];
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i]!;
    if (quoted) {
      if (char === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (char === '"') {
        quoted = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === ',') {
      record.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') {
        i++;
      }
      record.push(field);
      records.push(record);
      record = [];
      field = '';
    } else {
      field += char;
    }
  }
  if (field !== '' || record.length > 0) {
    record.push(field);
    records.push(record);
  }
  const [header, ...body] = records.filter((row) => row.some((cell) => cell !== ''));
  if (!header) {
    return [];
  }
  return body.map((row) => Object.fromEntries(header.map((name, index) => [name.trim(), (row[index] ?? '').trim()])));
}

/** The latest observation among rows matching every given dimension, in percent as published. */
export function latest(rows: readonly CsvRow[], match: Readonly<Record<string, string>>): Observation | undefined {
  let best: Observation | undefined;
  for (const row of rows) {
    if (!Object.entries(match).every(([key, value]) => row[key] === value)) {
      continue;
    }
    const raw = row.OBS_VALUE ?? '';
    const value = Number(raw);
    const asOf = row.TIME_PERIOD ?? '';
    if (raw === '' || !Number.isFinite(value) || asOf === '') {
      continue;
    }
    if (!best || asOf > best.asOf) {
      best = { value, asOf };
    }
  }
  return best;
}

/** The value of a daily series in force on a date (the last observation on or before it). */
function onOrBefore(rows: readonly CsvRow[], match: Readonly<Record<string, string>>, date: string) {
  return latest(
    rows.filter((row) => (row.TIME_PERIOD ?? '') <= date),
    match,
  );
}

/** The first period of the run of identical values that ends at the latest observation, if it began in the window. */
function inForceSince(rows: readonly CsvRow[], match: Readonly<Record<string, string>>): string | undefined {
  const series = rows
    .filter((row) => Object.entries(match).every(([key, value]) => row[key] === value) && row.OBS_VALUE)
    .map((row) => ({ asOf: row.TIME_PERIOD ?? '', value: Number(row.OBS_VALUE) }))
    .sort((a, b) => (a.asOf < b.asOf ? -1 : 1));
  const last = series.at(-1);
  if (!last) {
    return undefined;
  }
  let first = series.length - 1;
  while (first > 0 && series[first - 1]!.value === last.value) {
    first--;
  }
  // The run reaching the start of the window means the change happened before it.
  return first === 0 ? undefined : series[first]!.asOf;
}

const inRange = (value: number | undefined, min: number, max: number) =>
  value !== undefined && Number.isFinite(value) && value >= min && value <= max ? value : undefined;

/** Converts a published percentage to a fraction, rejecting values outside a plausible range. */
const rate = (percent: number | undefined, min = 0, max = 0.2) =>
  inRange(percent === undefined ? undefined : round(percent / 100), min, max);

const endOfMonth = (period: string) => `${period}-31`;

export interface BoiFiles {
  readonly br: string;
  readonly zcm: string;
  readonly mortgages: string;
  readonly loans: string;
}

export function marketFromBoi(files: Partial<BoiFiles>, fetchedAt: Date): LiveMarket {
  const br = parseCsv(files.br ?? '');
  const zcm = parseCsv(files.zcm ?? '');
  const mortgages = parseCsv(files.mortgages ?? '');
  const loans = parseCsv(files.loans ?? '');
  const missing: string[] = [];

  const BOI_RATE = { SERIES_CODE: 'MNT_RIB_BOI_D' };
  const boiObservation = latest(br, BOI_RATE);
  const boiValue = rate(boiObservation?.value, 0, 0.15);
  const boiRate = boiObservation && boiValue !== undefined ? { value: boiValue, asOf: boiObservation.asOf } : undefined;
  const prime = boiRate
    ? { value: round(boiRate.value + PRIME_SPREAD), asOf: boiRate.asOf, since: inForceSince(br, BOI_RATE) }
    : undefined;
  if (!prime) {
    missing.push('prime');
  }

  const monthly = (code: string, label: string, min: number, max: number): Observation | undefined => {
    const observation = latest(zcm, { SERIES_CODE: code });
    const value = rate(observation?.value, min, max);
    if (!observation || value === undefined) {
      missing.push(label);
      return undefined;
    }
    return { value, asOf: observation.asOf };
  };
  const inflationExpectation = monthly('EXP_EXB_ZD_5Y_MA', 'inflationExpectation', -0.05, 0.15);
  const bondYield5y = monthly('ZC_TSB_ZND_05Y_MA', 'bondYield5y', -0.02, 0.15);
  const linkedYield5y = monthly('ZC_TSB_ZRD_05Y_MA', 'linkedYield5y', -0.05, 0.1);

  const history = housingHistory(mortgages, br);

  // The latest month the fixed unlinked average covers; every housing figure is read for that one
  // month, since mixing months would compare a September fixed rate with an August variable one.
  const anchorMonth = history.at(-1)?.month;
  let housing: LiveMarket['housing'];
  if (anchorMonth) {
    housing = { asOf: anchorMonth, ...housingMonth(mortgages, anchorMonth) };
    for (const key of ['fixedUnlinked', 'fixedLinked', 'variableLinked', 'primeMargin'] as const) {
      if (housing[key] === undefined) {
        missing.push(`housing.${key}`);
      }
    }
  } else {
    missing.push('housing');
  }

  let allPurpose: LiveMarket['allPurpose'];
  const average = (item: string, indexation: string, kind: string, dataType: string) =>
    bandAverage(mortgages, item, indexation, kind, dataType);
  const apMonth = average('A2B2', 'NI', 'F', 'R')?.asOf;
  if (apMonth) {
    const at = (observation: Observation | undefined) =>
      observation && observation.asOf === apMonth ? observation.value : undefined;
    const benchmark = at(average('A2B2', 'NI', 'V', 'RB'));
    const margin = at(average('A2B2', 'NI', 'V', 'RM'));
    const boiThen = onOrBefore(br, BOI_RATE, endOfMonth(apMonth));
    const primeThen = boiThen ? boiThen.value / 100 + PRIME_SPREAD : undefined;
    const variable = benchmark !== undefined && margin !== undefined ? (benchmark + margin) / 100 : undefined;
    allPurpose = {
      asOf: apMonth,
      fixedUnlinked: rate(at(average('A2B2', 'NI', 'F', 'R')), 0.01, 0.25),
      variableMarginOverPrime:
        variable !== undefined && primeThen !== undefined
          ? inRange(round(variable - primeThen), -0.02, 0.08)
          : undefined,
    };
    for (const key of ['fixedUnlinked', 'variableMarginOverPrime'] as const) {
      if (allPurpose[key] === undefined) {
        missing.push(`allPurpose.${key}`);
      }
    }
  } else {
    missing.push('allPurpose');
  }

  // Households outside private banking — the population a consolidation borrower belongs to.
  const consumerObservation = latest(loans, {
    BS_ITEM: 'A2B1',
    COUNTERPART_SECTOR: 'S_X306_L',
    INDEXATION_TYPE: 'NI',
    DATA_TYPE: 'R',
  });
  const consumerValue = rate(consumerObservation?.value, 0.01, 0.3);
  const consumerUnsecured =
    consumerObservation && consumerValue !== undefined ? { value: consumerValue, asOf: consumerObservation.asOf } : undefined;
  if (!consumerUnsecured) {
    missing.push('consumerUnsecured');
  }

  return {
    fetchedAt: fetchedAt.toISOString(),
    boiRate,
    prime,
    inflationExpectation,
    bondYield5y,
    linkedYield5y,
    housing,
    allPurpose,
    history: history.length > 0 ? history : undefined,
    consumerUnsecured,
    missing,
  };
}

/**
 * The latest observation of a mortgage series averaged across every LTV, payment-to-income and
 * property-value band. A series is published for the whole population (PTI band P01) where the
 * bank reports one; unlinked variable housing loans appear only under band P07, taken when P01 is
 * absent.
 */
function bandAverage(rows: readonly CsvRow[], item: string, indexation: string, kind: string, dataType: string) {
  const base = { BS_ITEM: item, INDEXATION_TYPE: indexation, IR_FV_TYPE: kind, DATA_TYPE: dataType, LTV: 'L0' };
  return latest(rows, { ...base, PTI: 'P01' }) ?? latest(rows, { ...base, PTI: 'P07' });
}

/** The housing averages for one month, each validated; the headline figures and the history share it. */
function housingMonth(mortgages: readonly CsvRow[], month: string) {
  const rows = mortgages.filter((row) => row.TIME_PERIOD === month);
  const value = (indexation: string, kind: string, dataType: string) =>
    bandAverage(rows, 'A2C', indexation, kind, dataType)?.value;
  const benchmark = value('CPI', 'V', 'RB');
  const margin = value('CPI', 'V', 'RM');
  return {
    fixedUnlinked: rate(value('NI', 'F', 'R'), 0.005),
    fixedLinked: rate(value('CPI', 'F', 'R'), 0, 0.15),
    variableLinked: benchmark !== undefined && margin !== undefined ? rate(benchmark + margin, 0, 0.15) : undefined,
    primeMargin: rate(value('NI', 'V', 'RM'), -0.02, 0.03),
  };
}

/**
 * The housing averages month by month, with prime at each month end. Each month is read on its
 * own, so a series that misses a month leaves a gap there rather than borrowing a neighbour's value.
 */
function housingHistory(mortgages: readonly CsvRow[], br: readonly CsvRow[]): MarketMonth[] {
  const isFixedUnlinked = (row: CsvRow) =>
    row.BS_ITEM === 'A2C' && row.INDEXATION_TYPE === 'NI' && row.IR_FV_TYPE === 'F' && row.DATA_TYPE === 'R';
  const months = [...new Set(mortgages.filter((row) => isFixedUnlinked(row) && row.OBS_VALUE).map((row) => row.TIME_PERIOD ?? ''))]
    .filter((month) => /^\d{4}-\d{2}$/.test(month))
    .sort()
    .slice(-HISTORY_MONTHS);

  return months.map((month) => {
    const { fixedUnlinked, fixedLinked, variableLinked } = housingMonth(mortgages, month);
    const boi = rate(onOrBefore(br, { SERIES_CODE: 'MNT_RIB_BOI_D' }, endOfMonth(month))?.value, 0, 0.15);
    return {
      month,
      prime: boi === undefined ? undefined : round(boi + PRIME_SPREAD),
      fixedUnlinked,
      fixedLinked,
      variableLinked,
    };
  });
}

/** Rounds away floating-point noise from percent-to-fraction conversion. */
function round(value: number): number {
  return Math.round(value * 1e8) / 1e8;
}

/** True when at least the prime rate or the housing averages came through. */
export function isUsable(market: LiveMarket): boolean {
  return market.prime !== undefined || market.housing !== undefined;
}
